import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Tune from "@mui/icons-material/Tune";

import IconButton from "features/components/IconButton";
import Menu from "features/components/Menu";
import RadioGroup, { RadioSelectOption } from "features/components/RadioGroup";

interface TableSettingsButtonProps<T extends string> {
  countType: T;
  onCountTypeChange: (value: T) => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  countTypeOptions: RadioSelectOption[];
}

const EventsSmartGroupsTableSettings = <T extends string>({
  countType,
  onCountTypeChange,
  isOpen,
  setIsOpen,
  countTypeOptions,
}: TableSettingsButtonProps<T>) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
    setIsOpen(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setIsOpen(false);
  };

  // use "open" prop to forcefully "close" the dropdown
  // we can't forcefully open it as we might not have anchorEl ready (if open was a default on 1st render)
  useEffect(() => {
    if (!isOpen) {
      handleClose();
    } else if (isOpen && !anchorEl) {
      // If we want to open but don't have an anchor, use the trigger ref
      setAnchorEl(triggerRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, anchorEl]);

  const handleCountTypeChange = (value: string) => {
    onCountTypeChange(value as T);
  };

  return (
    <div>
      <div
        ref={triggerRef}
        data-testid="table-settings-button"
        aria-controls={isOpen ? "table-settings-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={isOpen ? "true" : undefined}
        onClick={handleClick}
        className="inline-block"
      >
        <IconButton size="small" color="secondary">
          <Tune fontSize="small" />
        </IconButton>
      </div>
      <Menu
        id="table-settings-menu"
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorEl={anchorEl}
        marginThreshold={0}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: "white",
              boxShadow:
                "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
              border: "1px solid #e5e7eb",
            },
          },
          list: {
            sx: {
              py: 0,
              zIndex: 999,
            },
            "aria-labelledby": "table-settings-menu",
          },
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transitionDuration={0}
        disableRestoreFocus
        disableScrollLock
        container={document.body}
      >
        <div
          className="flex flex-col text-sm!"
          onKeyDown={(event: any) => {
            // needed so that Menu doesn't default to "go-to MenuItem logic" when we type
            event.stopPropagation();
          }}
        >
          <div className="p-3 min-w-48">
            <div className="text-sm font-medium mb-3 text-gray-700">
              {t("pages:dashboard.table_settings.title")}
            </div>

            <div className="text-sm mb-2 pb-2 border-b border-gray-300 text-gray-600">
              {t("pages:dashboard.table_settings.prioritize_by")}
            </div>
            <RadioGroup
              options={countTypeOptions}
              selected={countType}
              onSelect={handleCountTypeChange}
            />
          </div>
        </div>
      </Menu>
    </div>
  );
};

export default EventsSmartGroupsTableSettings;
